import React from 'react';
import TopToolbar from '../../global/subcomponents/topToolbar/TopToolbar';
import { prettifyColumn } from './constants';
import { CircularProgress, InputAdornment, TablePagination, TableSortLabel, TextField } from '@mui/material';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import './ImportedFiles.css';

class ImportedFileDetail extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      searchText: '',
      page: 0,
      rowsPerPage: 50,
      orderBy: null,
      order: 'asc',
    };
  }

  componentDidUpdate(prevProps) {
    // Reset to the first page when a different file is opened.
    if (prevProps.filename !== this.props.filename) {
      this.setState({ page: 0, searchText: '', orderBy: null, order: 'asc' });
    }
  }

  handleSort = (property) => () => {
    const isAsc = this.state.orderBy === property && this.state.order === 'asc';
    this.setState({ order: isAsc ? 'desc' : 'asc', orderBy: property, page: 0 });
  };

  descendingComparator = (a, b, orderBy) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];
    const aNum = parseFloat(aValue);
    const bNum = parseFloat(bValue);
    const bothNumeric =
      !isNaN(aNum) && !isNaN(bNum) &&
      isFinite(aValue) !== false && isFinite(bValue) !== false &&
      String(aValue).trim() !== '' && String(bValue).trim() !== '';
    if (bothNumeric) {
      aValue = aNum;
      bValue = bNum;
    } else {
      aValue = aValue === undefined || aValue === null ? '' : String(aValue).toLowerCase();
      bValue = bValue === undefined || bValue === null ? '' : String(bValue).toLowerCase();
    }
    if ((bValue < aValue) || (!bValue && aValue)) return -1;
    if ((bValue > aValue) || (bValue && !aValue)) return 1;
    return 0;
  };

  stableSort = (array, orderBy, order) => {
    if (!array || !orderBy) return array;
    const stabilized = array.map((el, index) => [el, index]);
    stabilized.sort((a, b) => {
      const cmp = order === 'desc'
        ? this.descendingComparator(a[0], b[0], orderBy)
        : -this.descendingComparator(a[0], b[0], orderBy);
      if (cmp !== 0) return cmp;
      return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
  };

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: parseInt(event.target.value, 10), page: 0 });
  };

  handleSearchChange = (value) => {
    this.setState({ searchText: value, page: 0 });
  };

  getContextualMenuItems = () => [
    {
      title: 'Back to Files',
      icon: <KeyboardBackspaceIcon />,
      onClick: () => this.props.onBack(),
    },
    {
      title: 'Edit / Rename File',
      icon: <EditOutlinedIcon />,
      onClick: () => this.props.onRename(),
    },
    {
      title: 'Delete File',
      icon: <DeleteOutlineIcon />,
      onClick: () => this.props.onDelete(),
    },
  ];

  getFilteredRows = () => {
    const { rows, headers } = this.props;
    const search = this.state.searchText.trim().toLowerCase();
    if (!search) return rows;
    return rows.filter((row) =>
      headers.some((h) =>
        (row[h] || '').toString().toLowerCase().includes(search)
      )
    );
  };

  render() {
    const { marketplace, filename, headers, rows, loading } = this.props;
    const filteredRows = this.getFilteredRows();
    const { page, rowsPerPage, orderBy, order } = this.state;
    const sortedRows = this.stableSort(filteredRows, orderBy, order);
    const pagedRows = sortedRows.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <React.Fragment>
        <TopToolbar
          pageName={marketplace}
          menuItems={this.getContextualMenuItems()}
        />

        <div className="imported-detail-bar">
          <div className="imported-detail-title">
            <div className="imported-detail-title-icon">
              <InsertDriveFileOutlinedIcon />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="imported-detail-filename" title={filename}>
                {filename}
              </div>
              <div className="imported-detail-chips">
                <span className="imported-detail-chip">{marketplace}</span>
                <span className="imported-detail-chip">
                  {rows.length} {rows.length === 1 ? 'row' : 'rows'}
                </span>
                <span className="imported-detail-chip">
                  {headers.length}{' '}
                  {headers.length === 1 ? 'column' : 'columns'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="imported-detail-toolbar">
          <TextField
            className="imported-detail-search"
            size="small"
            placeholder="Search rows..."
            variant="outlined"
            value={this.state.searchText}
            onChange={(e) => this.handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#90999c' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 280 }}
          />
          <div style={{ flex: 1 }} />
          {!loading && (
            <TablePagination
              component="div"
              labelRowsPerPage="Rows per page"
              rowsPerPageOptions={[
                5,
                25,
                50,
                100,
                { value: filteredRows.length || 1, label: 'All' },
              ]}
              count={filteredRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={this.handleChangePage}
              onRowsPerPageChange={this.handleChangeRowsPerPage}
            />
          )}
        </div>

        {loading ? (
          <div className="imported-files-loading">
            <CircularProgress sx={{ color: '#005e73' }} />
          </div>
        ) : (
          <React.Fragment>
            <div className="imported-grid-wrap">
              <table className="imported-grid">
                <thead>
                  <tr>
                    <th className="imported-grid-rownum">#</th>
                    {headers.map((h) => (
                      <th key={h}>
                        <TableSortLabel
                          active={orderBy === h}
                          direction={orderBy === h ? order : 'asc'}
                          onClick={this.handleSort(h)}
                        >
                          {prettifyColumn(h)}
                        </TableSortLabel>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((row, idx) => (
                    <tr key={row.__rowId || idx}>
                      <td className="imported-grid-rownum">
                        {page * rowsPerPage + idx + 1}
                      </td>
                      {headers.map((h) => (
                        <td key={h} title={row[h] || ''}>
                          {row[h] || ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="imported-grid-footer">
              {filteredRows.length === 0
                ? 'No rows to display'
                : `Showing ${page * rowsPerPage + 1}–${Math.min(
                    (page + 1) * rowsPerPage,
                    filteredRows.length
                  )} of ${filteredRows.length}${
                    this.state.searchText.trim()
                      ? ` (filtered from ${rows.length})`
                      : ''
                  } rows`}
            </div>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
}

export default ImportedFileDetail;
